import { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { PaymentsResponse } from '../lib/types';
import { formatCurrency, formatCount, formatMonth, formatPercent } from '../lib/format';
import {
  ErrorState,
  LoadingState,
  MonthPicker,
  MonthsSelect,
  PageHeader,
  Section,
  StatCard,
  TableScroll,
  useInsightsQuery,
} from '../components/ui';

const EMPTY_SPLIT = {
  volume: 0,
  transactions: 0,
  feesCollected: 0,
  interchangeEstimate: 0,
  justifiEstimate: 0,
  rutaNetEstimate: 0,
  cardCost: 0,
  rutaNet: 0,
};

export function PaymentsPage() {
  const [months, setMonths] = useState(6);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const { data, isLoading, error } = useInsightsQuery<PaymentsResponse>(
    ['payments', months],
    `/payment-economics?months=${months}`,
  );

  if (isLoading) return <LoadingState />;
  if (error || !data) return <ErrorState error={error} />;

  const activeMonth = selectedMonth && data.months.includes(selectedMonth) ? selectedMonth : null;

  // Platform figures: window totals, or a single month when one is picked.
  const totals = activeMonth
    ? data.platform.byMonth.find((m) => m.month === activeMonth) ?? EMPTY_SPLIT
    : data.platform.totals;
  const orgFiguresFor = (org: (typeof data.organizations)[number]) =>
    activeMonth ? org.byMonth.find((m) => m.month === activeMonth) ?? EMPTY_SPLIT : org.totals;

  const effectiveRate = totals.volume > 0 ? totals.feesCollected / totals.volume : null;
  const rutaShare = totals.feesCollected > 0 ? totals.rutaNet / totals.feesCollected : null;

  const chartData = data.platform.byMonth.map((m) => ({
    month: formatMonth(m.month),
    Interchange: m.interchangeEstimate,
    Justifi: m.justifiEstimate,
    Ruta: m.rutaNetEstimate,
  }));

  const { feeModel } = data;

  return (
    <>
      <PageHeader
        title="Payments (Justifi)"
        subtitle="Card volume from paying organizations, and what we keep on it"
        actions={
          <>
            <MonthsSelect value={months} onChange={setMonths} />
            <MonthPicker months={data.months} value={activeMonth} onChange={setSelectedMonth} />
          </>
        }
      />

      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label={activeMonth ? `Volume processed (${formatMonth(activeMonth)})` : `Volume processed (${months} mo)`}
          value={formatCurrency(totals.volume)}
          hint={`${formatCount(totals.transactions)} transactions`}
        />
        <StatCard
          label="Fees collected from orgs"
          value={formatCurrency(totals.feesCollected)}
          hint={effectiveRate === null ? undefined : `${formatPercent(effectiveRate, 2)} effective rate (actual)`}
        />
        <StatCard
          label="Card net to Ruta"
          value={formatCurrency(totals.rutaNet)}
          hint={rutaShare === null ? undefined : `${formatPercent(rutaShare)} of fees collected`}
        />
        <StatCard
          label={`Card cost (${data.cardCostPercent}% all-in)`}
          value={formatCurrency(totals.cardCost)}
          hint="Off the ledger rate, not an interchange estimate"
        />
      </div>

      <Section
        title="Fee split by month"
        subtitle="Stack = fees collected from orgs (actual); split per the fee model below"
      >
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} tickFormatter={(v: number) => `$${v}`} />
            <Tooltip formatter={(value: number) => formatCurrency(value)} />
            <Legend />
            <Bar dataKey="Interchange" stackId="fees" fill="#d6d3d1" />
            <Bar dataKey="Justifi" stackId="fees" fill="#a8a29e" />
            <Bar dataKey="Ruta" stackId="fees" fill="#486948" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <p className="mt-3 text-xs text-stone-400">
          Fee model ({feeModel.source === 'default' ? 'built-in defaults' : `source: ${feeModel.source}`}):
          interchange {(feeModel.interchangeBps / 100).toFixed(2)}% + {formatCurrency(feeModel.interchangeFixed)}/txn (blended estimate),
          Justifi {(feeModel.justifiBps / 100).toFixed(2)}% + {formatCurrency(feeModel.justifiFixed)}/txn (contracted rate).
          Volume and fees collected are actuals from Justifi payment data, and cover paying
          customers only — Home's Ruta Pay spread also counts a company invoiced that month but not paying today. The
          interchange / Justifi split above is a model; the headline card net is measured
          against the {data.cardCostPercent}% all-in ledger rate instead, so it does not
          move when the split is re-estimated.
        </p>
      </Section>

      <Section
        title="By organization"
        subtitle={activeMonth ? `For ${formatMonth(activeMonth)}` : `Totals over the last ${months} months`}
      >
        <TableScroll>
          <table className="w-full min-w-[760px] whitespace-nowrap text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-left text-[11px] uppercase tracking-wider text-stone-400">
                <th className="py-2 font-medium">Organization</th>
                <th className="py-2 font-medium">Contracted rate</th>
                <th className="py-2 text-right font-medium">Volume</th>
                <th className="py-2 text-right font-medium">Txns</th>
                <th className="py-2 text-right font-medium">Fees collected</th>
                <th className="py-2 text-right font-medium">Interchange (est.)</th>
                <th className="py-2 text-right font-medium">Justifi (est.)</th>
                <th className="py-2 text-right font-medium">Ruta net (est.)</th>
              </tr>
            </thead>
            <tbody>
              {data.organizations
                .map((org) => ({ org, f: orgFiguresFor(org) }))
                .sort((a, b) => b.f.volume - a.f.volume)
                .map(({ org, f }) => (
                  <tr key={org.orgId} className="border-b border-stone-100">
                    <td className="py-2 font-medium text-stone-900">
                      {org.name}
                      {!org.isPaying && (
                        <span className="ml-1.5 text-[10px] font-normal uppercase tracking-wide text-stone-400">
                          not counted
                        </span>
                      )}
                    </td>
                    <td className="tabular py-2 text-xs text-stone-500">
                      {org.contractedRates.length > 0
                        ? org.contractedRates
                            .map((r) => `${(r.basisPointRate / 100).toFixed(2)}% + ${formatCurrency(r.transactionFeeCents / 100)} (${r.rateType})`)
                            .join(', ')
                        : '—'}
                    </td>
                    <td className="tabular py-2 text-right text-stone-700">{formatCurrency(f.volume)}</td>
                    <td className="tabular py-2 text-right text-stone-700">{formatCount(f.transactions)}</td>
                    <td className="tabular py-2 text-right font-medium text-stone-900">{formatCurrency(f.feesCollected)}</td>
                    <td className="tabular py-2 text-right text-stone-500">{formatCurrency(f.interchangeEstimate)}</td>
                    <td className="tabular py-2 text-right text-stone-500">{formatCurrency(f.justifiEstimate)}</td>
                    <td className="tabular py-2 text-right font-medium text-primary">{formatCurrency(f.rutaNetEstimate)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </TableScroll>
      </Section>
    </>
  );
}
